/**
 * Copyright (c) Qwoach LLC 2025. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Artem Bogush <artem@qwoach.com> on 06.06.25
 *
 */
import dayjs from "dayjs";
import {ServiceUsageType} from "@shared/Invoice/ServiceUsageType";
import {DEFAULT_QUOTE_SECTION_ID, QuoteSectionId} from "@shared/Quote/QuoteSectionId";

export const TRIP_RATE_DEFAULT = 0.25
export const TRIP_RATE_DEFAULT_CENTS = 25
export const BOOK_TLSW_TIME_HOURS_DEFAULT = 12
export const BOOK_AAM_TIME_HOURS_DEFAULT = 4
export const ASF_FINANCIAL_YEAR_START_MS_DEFAULT = dayjs("April 01 1970", "MMMM DD YYYY").utc().valueOf() // 1st April
export const FINANCIAL_YEAR_START_MS_DEFAULT = ASF_FINANCIAL_YEAR_START_MS_DEFAULT

export const QuoteRateType = Object.freeze({
	DIRECT_SESSION_RATE: 'DIRECT_SESSION_RATE',
	ADMIN_RATE: 'ADMIN_RATE',
	TRAVEL_RATE: 'TRAVEL_RATE',
	CUSTOM_RATE_1: 'CUSTOM_RATE_1',
	CUSTOM_RATE_2: 'CUSTOM_RATE_2',
	CUSTOM_RATE_3: 'CUSTOM_RATE_3',
	CUSTOM_RATE_4: 'CUSTOM_RATE_4',
	CUSTOM_RATE_5: 'CUSTOM_RATE_5',
})

export const STANDARD_RATE_TYPES = [
	QuoteRateType.DIRECT_SESSION_RATE,
	QuoteRateType.ADMIN_RATE,
	QuoteRateType.TRAVEL_RATE,
]

export const CUSTOM_RATE_TYPES = [
	QuoteRateType.CUSTOM_RATE_1,
	QuoteRateType.CUSTOM_RATE_2,
	QuoteRateType.CUSTOM_RATE_3,
	QuoteRateType.CUSTOM_RATE_4,
	QuoteRateType.CUSTOM_RATE_5,
]

export const MAX_CUSTOM_RATES = CUSTOM_RATE_TYPES.length

// console.log("ServiceType.js: ASF_FINANCIAL_YEAR_START_MS_DEFAULT = ", ASF_FINANCIAL_YEAR_START_MS_DEFAULT);


export const ServiceType = Object.freeze({
	SESSION: 'SESSION',

	DIRECT_SESSION_RATE: 'DIRECT_SESSION_RATE',
	ADMIN_RATE: 'ADMIN_RATE',
	// DIRECT_SESSION: 'DIRECT_SESSION',
	// ADMIN_WORK: 'ADMIN_WORK',

	CODING: 'CODING',
	BIRTH_FAMILY_WORK_FORMULATION: 'BIRTH_FAMILY_WORK_FORMULATION',

	NON_CLINICAL_COSTS: 'NON_CLINICAL_COSTS',
	REPORT_WRITING: 'REPORT_WRITING',
	PREPARATION_WRITEUP_EXTRA: 'PREPARATION_WRITEUP_EXTRA',

	SESSION_PREPARATION_TIME: 'SESSION_PREPARATION_TIME',
	ROUND_TRIP_MILEAGE: 'ROUND_TRIP_MILEAGE',
	BIRTH_PARENT_1_MILEAGE: 'BIRTH_PARENT_1_MILEAGE',
	BIRTH_PARENT_2_MILEAGE: 'BIRTH_PARENT_2_MILEAGE',
	BIRTH_PARENT_3_MILEAGE: 'BIRTH_PARENT_3_MILEAGE',
	BIRTH_PARENT_4_MILEAGE: 'BIRTH_PARENT_4_MILEAGE',
	TRIP_RATE: 'TRIP_RATE',
	VENUE_HIRE: 'VENUE_HIRE',
	PARKING_COST: 'PARKING_COST',
	MATERIALS_AND_EQUIPMENT: 'MATERIALS_AND_EQUIPMENT',

	TRAVEL: 'TRAVEL',
	TLSW_BOOK_WRITING: 'TLSW_BOOK_WRITING',
	AAM_BOOK_WRITING: 'AAM_BOOK_WRITING',

	TLSW_BOOK_WRITING_MAX_TIME: 'TLSW_BOOK_WRITING_MAX_TIME',
	AAM_BOOK_WRITING_MAX_TIME: 'AAM_BOOK_WRITING_MAX_TIME',

	INVOICE_TOTAL_AMOUNT: 'INVOICE_TOTAL_AMOUNT',
	FIXED_FEE: 'FIXED_FEE',
	TIME_BASED: 'TIME_BASED',
	TRAVEL_DISTANCE: 'TRAVEL_DISTANCE',

	NUMBER_OF_BIRTH_FAMILY_MEMBERS: 'NUMBER_OF_BIRTH_FAMILY_MEMEBRS',
	EMPTY_PLACEHOLDER: 'EMPTY_PLACEHOLDER',

})

export const ServiceGroup = Object.freeze({
	RATES: 'RATES',
	SESSIONS: 'SESSIONS',
	BIRTH_FAMILY_WORK: 'BIRTH_FAMILY_WORK',
	BOOK_WRITING: 'BOOK_WRITING',
})

export const ServiceDescriptionEnum = Object.freeze({
	[ServiceType.SESSION]: {unit: "hour", category: "service", title: "Session", placeholder: "Your hourly rate", description: ""},
	[ServiceType.DIRECT_SESSION_RATE]: 				{unit: "hour", category: "service", title: "Direct Contact Hourly Rate", placeholder: "Your hourly rate", description: "Includes direct contact with family, like sessions, professional meetings, liaison calls with family, etc."},
	[ServiceType.ADMIN_RATE]: 					{unit: "hour", category: "service", title: "Non-contact Hourly Rate", placeholder: "Your hourly rate", description: "Does not involve direct contact with the family. \nIncludes: session preparation/write-up, book writing and coding."},
	// [ServiceType.ADMIN_WORK]: 					{unit: "hour", category: "service", title: "Non-contact Hourly Rate", placeholder: "Your hourly rate", description: "Does not involve direct contact with the family. \nIncludes: session preparation/write-up, book writing and coding."},
	[ServiceType.NON_CLINICAL_COSTS]: 			{unit: "hour", category: "service", title: "Non-Clinical Costs", placeholder: "Your hourly rate", description: "Your hourly rate for the costs that are not directly related to clinical services."},
	[ServiceType.REPORT_WRITING]: 				{unit: "hour", category: "service", title: "Report Writing", placeholder: "Your hourly rate", description: "Your hourly rate for writing reports related to the case."},
	[ServiceType.FIXED_FEE]: 					{unit: "", category: "service", title: "Fixed Fee", placeholder: "Total amount", description: ""},
	[ServiceType.TIME_BASED]: 					{unit: "hour", category: "service", title: "Time-Based Service", placeholder: "Your hourly rate", description: ""},
	[ServiceType.TRAVEL_DISTANCE]: 				{unit: "mile", category: "expense", title: "Travel Distance", placeholder: "Distance in miles", description: ""},
	[ServiceType.SESSION_PREPARATION_TIME]: 	{unit: "hour", category: "service", title: "Preparation/Write up", placeholder: "How many hours per session?", description: "How much extra time you charge for preparation and writeup for each session?"},
	[ServiceType.TLSW_BOOK_WRITING_MAX_TIME]: 	{unit: "hour", category: "service", title: "TLSW Book Writing Total Time", placeholder: "How many hours to write a TLSW book?", description: "Total number of hours to complete a TLSW book."},
	[ServiceType.AAM_BOOK_WRITING_MAX_TIME]: 	{unit: "hour", category: "service", title: "All About Me Book Writing Total Time", placeholder: "How many hours to write an All About Me book?", description: "Total number of hours to complete an All About Me book."},
	[ServiceType.ROUND_TRIP_MILEAGE]: 			{unit: "mile", category: "expense", title: "Commission Location Round Trip Distance", placeholder: "Round-trip distance", description: "Round trip distance to the yong person's location"},
	[ServiceType.TRIP_RATE]: 					{unit: "", 	category: "service", title: "Trip Covered Amount", placeholder: "Trip covered amount", description: "Covered amount per mile"},
	[ServiceType.VENUE_HIRE]: 					{unit: "session", category: "service", title: "Venue Hire", placeholder: "Cost per session", description: "Cost of hiring a venue for a session.", options: {totalPerSessionSwitch: true}},
	[ServiceType.MATERIALS_AND_EQUIPMENT]: 		{unit: "session", category: "service", title: "Materials and Equipment", placeholder: "Total Cost", description: "Total cost of materials and equipment for sessions.", options: {totalPerSessionSwitch: true}},


	[ServiceType.CODING]: 						{unit: "hour", category: "service", title: "Child's File Reading / Chronology", placeholder: "Your hourly rate", description: ""},
	[ServiceType.PARKING_COST]: 			{unit: "mile", category: "expense", title: "Parking Cost", placeholder: "", description: ""},
	[ServiceType.BIRTH_PARENT_1_MILEAGE]: 		{unit: "mile", category: "expense", title: "Birth Family Member #1 Round Trip Distance", placeholder: "", description: "Round trip distance to the Birth Family Member #1 location"},
	[ServiceType.BIRTH_PARENT_2_MILEAGE]: 		{unit: "mile", category: "expense", title: "Birth Family Member #2 Round Trip Distance", placeholder: "", description: "Round trip distance to the Birth Family Member #2 location"},
	[ServiceType.BIRTH_PARENT_3_MILEAGE]: 		{unit: "mile", category: "expense", title: "Birth Family Member #3 Round Trip Distance", placeholder: "", description: "Round trip distance to the Birth Family Member #3 location"},
	[ServiceType.BIRTH_PARENT_4_MILEAGE]: 		{unit: "mile", category: "expense", title: "Birth Family Member #4 Round Trip Distance", placeholder: "", description: "Round trip distance to the Birth Family Member #4 location"},
	[ServiceType.BIRTH_FAMILY_WORK_FORMULATION]:{unit: "hour", category: "service", title: "Birth Family Work Write Up / Formulation", placeholder: "", description: ""},

	[ServiceType.TLSW_BOOK_WRITING]:			{unit: "hour", category: "service", title: "TLSW Book Writing", placeholder: "", description: ""},
	[ServiceType.AAM_BOOK_WRITING]:				{unit: "hour", category: "service", title: "All About Me Book Writing", placeholder: "", description: ""},


})

export const BirthMemberMileageServices = [
	ServiceType.BIRTH_PARENT_1_MILEAGE,
	ServiceType.BIRTH_PARENT_2_MILEAGE,
	ServiceType.BIRTH_PARENT_3_MILEAGE,
	ServiceType.BIRTH_PARENT_4_MILEAGE,
]

export const birthFamilyWorkServices = [
	ServiceType.CODING,
	ServiceType.BIRTH_FAMILY_WORK_FORMULATION,
]

export const bookWritingServices = [
	ServiceType.TLSW_BOOK_WRITING,
	ServiceType.AAM_BOOK_WRITING,
]

export const travelCostServices = [
	ServiceType.ROUND_TRIP_MILEAGE,
	ServiceType.TRIP_RATE,
	...BirthMemberMileageServices,
]

export const extraServices = [
	ServiceType.NON_CLINICAL_COSTS,
	ServiceType.FIXED_FEE,
	ServiceType.TIME_BASED,
	ServiceType.REPORT_WRITING,
	ServiceType.VENUE_HIRE,
	ServiceType.MATERIALS_AND_EQUIPMENT,
	ServiceType.PARKING_COST,
	// ServiceType.PREPARATION_WRITEUP_EXTRA,

]

export const configurableServiceTypes = [
	ServiceType.SESSION,
	ServiceType.FIXED_FEE,
	ServiceType.TIME_BASED,
	ServiceType.TRAVEL_DISTANCE,
]



export const rateServices = [
	QuoteRateType.DIRECT_SESSION_RATE,
	QuoteRateType.ADMIN_RATE,
	QuoteRateType.TRAVEL_RATE,
]
export const rateServicesExtended = [
	...rateServices,
]

export const sessionServices = [
	ServiceType.SESSION,
]
export const coreTlswServices = [
	ServiceType.SESSION_PREPARATION_TIME,

	// ServiceType.ROUND_TRIP_MILEAGE,
]

export const mileageServices = [
	ServiceType.ROUND_TRIP_MILEAGE,
	ServiceType.TRAVEL_DISTANCE,
	ServiceType.BIRTH_PARENT_1_MILEAGE,
	ServiceType.BIRTH_PARENT_2_MILEAGE,
	ServiceType.BIRTH_PARENT_3_MILEAGE,
	ServiceType.BIRTH_PARENT_4_MILEAGE,
]

export const adminRateServices = [
	// ServiceType.MATERIALS_AND_EQUIPMENT,
	ServiceType.TIME_BASED,
	ServiceType.REPORT_WRITING,
	ServiceType.SESSION_PREPARATION_TIME,
	ServiceType.TLSW_BOOK_WRITING,
	ServiceType.AAM_BOOK_WRITING,
	ServiceType.CODING,
	ServiceType.BIRTH_FAMILY_WORK_FORMULATION,
	ServiceType.PREPARATION_WRITEUP_EXTRA,
]

export const directRateServices = [
	ServiceType.SESSION,
	// ServiceType.NON_CLINICAL_COSTS,

]

export const isDirectRateServices = (serviceType) => {
	return directRateServices.includes(serviceType)
}
export const isAdminRateServices = (serviceType) => {
	return adminRateServices.includes(serviceType)
}
export const isMileageServices = (serviceType) => {
	return mileageServices.includes(serviceType)
}
export const isPlainCostServices = (serviceType) => {
	return [
		ServiceType.NON_CLINICAL_COSTS,
		// ServiceType.REPORT_WRITING,
		ServiceType.VENUE_HIRE,
		ServiceType.MATERIALS_AND_EQUIPMENT,
	].includes(serviceType)
}
export const isRateServices = (serviceType) => {
	return rateServices.includes(serviceType)
}
export const isSessionService = (serviceType) => {
	return sessionServices.includes(serviceType)
}
export const isCoreTLSWServices = (serviceType) => {
	return coreTlswServices.includes(serviceType)
}

export const isBirthFamilyWorkServices = (serviceType) => {
	return birthFamilyWorkServices.includes(serviceType)
}
export const isBookWritingServices = (serviceType) => {
	return bookWritingServices.includes(serviceType)
}
export const isTravelCostServices = (serviceType) => {
	return travelCostServices.includes(serviceType)
}
export const isExtraServices = (serviceType) => {
	return extraServices.includes(serviceType)
}

export const getDefaultRateTypeForServiceType = (serviceType) => {
	if (isDirectRateServices(serviceType)) {
		return QuoteRateType.DIRECT_SESSION_RATE
	}
	if (isAdminRateServices(serviceType)) {
		return QuoteRateType.ADMIN_RATE
	}
	if (isMileageServices(serviceType)) {
		return QuoteRateType.TRAVEL_RATE
	}
	return null
}

export const getSectionIdForServiceType = (serviceType) => {
	if (serviceType === ServiceType.SESSION) {
		return QuoteSectionId.DIRECT_WORK
	}
	if (isCoreTLSWServices(serviceType)) {
		return QuoteSectionId.DIRECT_WORK
	}
	if (isBirthFamilyWorkServices(serviceType)) {
		return QuoteSectionId.INFO_GATHERING
	}
	if (isBookWritingServices(serviceType)) {
		return QuoteSectionId.BOOK_WRITING
	}
	if (isTravelCostServices(serviceType)) {
		return QuoteSectionId.TRAVEL
	}
	switch (serviceType) {
		case ServiceType.REPORT_WRITING:
			return QuoteSectionId.NON_DIRECT_WORK
		case ServiceType.NON_CLINICAL_COSTS:
		case ServiceType.VENUE_HIRE:
		case ServiceType.MATERIALS_AND_EQUIPMENT:
		case ServiceType.FIXED_FEE:
		case ServiceType.TIME_BASED:
		case ServiceType.PREPARATION_WRITEUP_EXTRA:
			return QuoteSectionId.OTHER_EXPENSES
		default:
			break
	}
	return DEFAULT_QUOTE_SECTION_ID
}


export const isServiceCategory = (serviceItem) => {
	let res = ServiceDescriptionEnum[serviceItem.serviceType]?.category === "service";
	console.log("ServiceItemView ServiceType.isServiceCategory: res = ", res);
	return res
}

export const defaultValueByServiceType = (serviceType) => {
	switch (serviceType) {
		case ServiceType.TRIP_RATE:
			return TRIP_RATE_DEFAULT;
		case ServiceType.TLSW_BOOK_WRITING_MAX_TIME:
			return BOOK_TLSW_TIME_HOURS_DEFAULT;
		case ServiceType.AAM_BOOK_WRITING_MAX_TIME:
			return BOOK_AAM_TIME_HOURS_DEFAULT;
		default:
			return null
	}
}

//indicates services that has max value limit and shows which service to check for the max value
export const ServiceToMaxValueMap = {
	[ServiceType.TLSW_BOOK_WRITING]: ServiceType.TLSW_BOOK_WRITING_MAX_TIME,
	[ServiceType.AAM_BOOK_WRITING]: ServiceType.AAM_BOOK_WRITING_MAX_TIME,
}

export const ServiceGroupNameEnum = Object.freeze({
	SESSIONS: 'Sessions',
	RATES: 'Rates',
	BIRTH_FAMILY_WORK: 'Information Gathering Work',
	BOOK_WRITING: 'Book Writing',
	TRAVEL_COSTS: 'Travel',
	EXTRA: 'Other Expenses',
})


const DirectSessionRateOptions = {
	serviceType: ServiceType.DIRECT_SESSION_RATE,
	serviceUsageType: ServiceUsageType.RATE,
	title: "Direct Contact Hourly Rate",
	itemPriceUnits: "hour",
	hasQuantity: false,
	hasPrice: true,
}
// Admin rate
const AdminRateOptions = {
	serviceType: ServiceType.ADMIN_RATE,
	serviceUsageType: ServiceUsageType.RATE,
	title: "Non-contact Hourly Rate",
	itemPriceUnits: "hour",
	hasQuantity: false,
	hasPrice: true,
};

const TripRateOptions = {
	serviceType: ServiceType.TRIP_RATE,
	serviceUsageType: ServiceUsageType.RATE,
	title: "Travel Distance Rate",
	itemPriceCents: TRIP_RATE_DEFAULT_CENTS,
	quantityUnits: "mile",
	hasQuantity: false,
	hasPrice: true,
};


const SessionOptions = {
	serviceType: ServiceType.SESSION,
	sectionId: QuoteSectionId.DIRECT_WORK,
	serviceUsageType: ServiceUsageType.QUANTITY_NUMBER,
	title: "Sessions",
	hasEditableTitle: true,
	hasQuantityNumber: true,
	quantityNumber: 18,
	quantityNumberUnits: "session",
	quantityNumberLabel: "Number of Sessions",
	quantity: 60,
	quantityLabel: "Duration (Minutes)",
	quantityUnits: "min",
	itemPriceUnits: "hour",
	conversionFactor: 1/60,
};

// Initial consult session

// Behaviour tree session


// Direct TLSW session

// Session preparation time
const SessionPreparationTimeOptions = {
	serviceType: ServiceType.SESSION_PREPARATION_TIME,
	sectionId: QuoteSectionId.DIRECT_WORK,
	serviceUsageType: ServiceUsageType.QUANTITY_NUMBER,
	title: "Preparation/Write up",
	hasQuantityNumber: true,
	quantityNumber: 1,
	quantityNumberUnits: "session",
	quantity: 1,
	quantityLabel: "Hours per Session",
	quantityUnits: "hour",
	itemPriceUnits: "hour",
	// conversionFactor: 1/60, // minutes to hours conversion
};

// Information Bank Work
const CodingOptions = {
	serviceType: ServiceType.CODING,
	sectionId: QuoteSectionId.INFO_GATHERING,
	serviceUsageType: ServiceUsageType.QUANTITY,
	title: "Child's File Reading / Chronology",
	quantity: 6,
	quantityUnits: "hour",
	itemPriceUnits: "hour",
};

// Information Gathering Work session



// Birth parent #1 mileage
const BirthParent1MileageOptions = {
	serviceType: ServiceType.BIRTH_PARENT_1_MILEAGE,
	sectionId: QuoteSectionId.TRAVEL,
	serviceUsageType: ServiceUsageType.QUANTITY,
	title: "Birth Family Member #1 Round Trip Distance",
	itemPriceCents: TRIP_RATE_DEFAULT_CENTS,
	itemPriceUnits: "mile",
	quantityUnits: "mile",
};

// Birth parent #2 mileage
const BirthParent2MileageOptions = {
	...BirthParent1MileageOptions,
	title: "Birth Family Member #2 Round Trip Distance",
	serviceType: ServiceType.BIRTH_PARENT_2_MILEAGE,
};
const BirthParent3MileageOptions = {
	...BirthParent1MileageOptions,
	title: "Birth Family Member #3 Round Trip Distance",
	serviceType: ServiceType.BIRTH_PARENT_3_MILEAGE,
};
const BirthParent4MileageOptions = {
	...BirthParent1MileageOptions,

	serviceType: ServiceType.BIRTH_PARENT_4_MILEAGE,
};

// Information Gathering Work formulation
const BirthFamilyWorkFormulationOptions = {
	serviceType: ServiceType.BIRTH_FAMILY_WORK_FORMULATION,
	sectionId: QuoteSectionId.INFO_GATHERING,
	serviceUsageType: ServiceUsageType.QUANTITY,
	title: "Birth Family Work Write Up / Formulation",
	quantity: 4,
	quantityUnits: "hour",
	itemPriceUnits: "hour",
};

// TLSW book writing
const TlswBookWritingOptions = {
	serviceType: ServiceType.TLSW_BOOK_WRITING,
	sectionId: QuoteSectionId.BOOK_WRITING,
	serviceUsageType: ServiceUsageType.QUANTITY,
	title: "TLSW Book Writing",
	quantity: 18,
	quantityUnits: "hour",
	itemPriceUnits: "hour",
};

// AAM book writing
const AamBookWritingOptions = {
	serviceType: ServiceType.AAM_BOOK_WRITING,
	sectionId: QuoteSectionId.BOOK_WRITING,
	serviceUsageType: ServiceUsageType.QUANTITY,
	title: "All About Me Book Writing",
	quantity: 6,
	quantityUnits: "hour",
	itemPriceUnits: "hour",
};

// Round trip mileage
const RoundTripMileageOptions = {
	serviceType: ServiceType.ROUND_TRIP_MILEAGE,
	sectionId: QuoteSectionId.TRAVEL,
	serviceUsageType: ServiceUsageType.QUANTITY_NUMBER,
	title: "Round Trip Distance",
	quantityNumberLabel: "Return Journeys",
	quantityNumberUnits: "return journey",
	hasQuantityNumber: true,
	itemPriceCents: TRIP_RATE_DEFAULT_CENTS,
	itemPriceUnits: "mile",
	quantityLabel: "Distance (Miles)",
	quantityUnits: "mile",
};



const ReportWritingOptions ={
	serviceType: ServiceType.REPORT_WRITING,
	sectionId: QuoteSectionId.NON_DIRECT_WORK,
	serviceUsageType: ServiceUsageType.QUANTITY,
	title: "Report Writing",
	hasQuantityNumber: false,
	hasQuantity: true,
	hasPrice: false,
	hasDescription: true,
	// quantity: 60,
	quantityUnits: "minute",
	itemPriceUnits: "hour",
	conversionFactor: 1/60, // minutes to hours conversion
}

const ExtraPreparationWriteupOptions ={
	serviceType: ServiceType.PREPARATION_WRITEUP_EXTRA,
	sectionId: QuoteSectionId.NON_DIRECT_WORK,
	serviceUsageType: ServiceUsageType.QUANTITY,
	title: "Extra Preparation/Write up",
	hasQuantityNumber: false,
	hasQuantity: true,
	hasPrice: false,
	hasDescription: true,
	// quantity: 60,
	quantityUnits: "minute",
	itemPriceUnits: "hour",
	conversionFactor: 1/60, // minutes to hours conversion
}

const ParkingCostOptions ={
	serviceType: ServiceType.PARKING_COST,
	sectionId: QuoteSectionId.TRAVEL,
	serviceUsageType: ServiceUsageType.PRICE,
	title: "Parking Cost",
	hasQuantity: false,
	hasPrice: true,
	hasDescription: true,
}

const NonClinicalCostsOptions ={
	serviceType: ServiceType.NON_CLINICAL_COSTS,
	sectionId: QuoteSectionId.OTHER_EXPENSES,
	serviceUsageType: ServiceUsageType.PRICE,
	title: "Non-Clinical Costs",
	hasQuantity: false,
	hasPrice: true,
	hasDescription: true,
}
const VenueHireOptions ={
	serviceType: ServiceType.VENUE_HIRE,
	sectionId: QuoteSectionId.OTHER_EXPENSES,
	serviceUsageType: ServiceUsageType.PRICE,
	title: "Venue Hire",
	hasQuantity: false,
	hasPrice: true,
	hasDescription: true,
}
const MaterialsOptions ={
	serviceType: ServiceType.MATERIALS_AND_EQUIPMENT,
	sectionId: QuoteSectionId.OTHER_EXPENSES,
	serviceUsageType: ServiceUsageType.PRICE,
	title: "Materials and Equipment",
	hasQuantity: false,
	hasPrice: true,
	hasDescription: true,
}

const FixedFeeOptions = {
	serviceType: ServiceType.FIXED_FEE,
	sectionId: QuoteSectionId.OTHER_EXPENSES,
	serviceUsageType: ServiceUsageType.PRICE,
	title: "Fixed Fee",
	hasEditableTitle: true,
	hasQuantity: false,
	hasPrice: true,
	hasDescription: false,
	priceLabel: "Total Amount",
}

const TimeBasedOptions = {
	serviceType: ServiceType.TIME_BASED,
	sectionId: QuoteSectionId.OTHER_EXPENSES,
	serviceUsageType: ServiceUsageType.QUANTITY,
	title: "Time-Based Service",
	hasEditableTitle: true,
	hasQuantity: true,
	hasPrice: false,
	hasDescription: false,
	quantity: 1,
	quantityLabel: "Duration (Hours)",
	quantityUnits: "hour",
	itemPriceUnits: "hour",
}

const TravelDistanceOptions = {
	serviceType: ServiceType.TRAVEL_DISTANCE,
	sectionId: QuoteSectionId.TRAVEL,
	serviceUsageType: ServiceUsageType.QUANTITY,
	title: "Travel Distance",
	hasEditableTitle: true,
	hasQuantity: true,
	hasPrice: false,
	hasDescription: false,
	quantity: 1,
	quantityLabel: "Distance (Miles)",
	quantityUnits: "mile",
	itemPriceUnits: "mile",
}



export const ServiceOptions = [
	SessionOptions,
	FixedFeeOptions,
	TimeBasedOptions,
	TravelDistanceOptions,
	ParkingCostOptions,
	MaterialsOptions,
	VenueHireOptions,
	NonClinicalCostsOptions,
	ReportWritingOptions,
	DirectSessionRateOptions,
	AdminRateOptions,
	TripRateOptions,
	SessionPreparationTimeOptions,
	CodingOptions,
	BirthParent1MileageOptions,
	BirthParent2MileageOptions,
	BirthParent3MileageOptions,
	BirthParent4MileageOptions,
	BirthFamilyWorkFormulationOptions,
	TlswBookWritingOptions,
	AamBookWritingOptions,
	RoundTripMileageOptions,
	ExtraPreparationWriteupOptions,
];

const _serviceOptionsMap = ServiceOptions.reduce((map, opt) => {
	if (opt && opt.serviceType) map[opt.serviceType] = opt;
	return map;
}, {});

export const getServiceOptionsByType = (serviceType) => {
	return _serviceOptionsMap[serviceType] || {};
};

export const serviceTitleByType = (serviceType) => {
	let serviceOptions = getServiceOptionsByType(serviceType);
	return serviceOptions?.title || "";
}
